/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component, ComponentFactoryResolver,
  ComponentRef, OnDestroy,
  OnInit, ViewChild, ViewContainerRef
} from '@angular/core';
import {
  Event, NavigationCancel, NavigationEnd,
  NavigationError, NavigationStart, Router
} from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from '../core/auth/auth.service';
import { MessageHeplerService } from '../core/message-helper/message-helper.service';
import { NotificationComponent } from './notification/notification.component';

@Component({
  selector: 'app-content-shell',
  templateUrl: './content-shell.component.html',
  styleUrls: ['./content-shell.component.scss']
})
export class ContentShellComponent implements OnInit, OnDestroy {
  @ViewChild('dynamicTokenExpiry', { read: ViewContainerRef }) private dynamicTokenExpiry: ViewContainerRef;
  @ViewChild('dynamicNotification', { read: ViewContainerRef }) private dynamicNotification: ViewContainerRef;

  loading = false;
  tokenExpiry$: Observable<boolean>;
  subscription: Subscription = new Subscription();
  notificationComponent: ComponentRef<NotificationComponent>;

  constructor(
    private router: Router,
    private authService: AuthService,
    public messageService: MessageHeplerService,
    private componentFactoryResolver: ComponentFactoryResolver
  ) {
    this.subscribeForRouteEvents();
    this.subscribeForTokenExpiry();
  }

  ngOnInit(): void {
    this.loadNotificationComponent();
  }

  /**
   * @name subscribeForRouteEvents
   * @description Function to subscribe for route events
   * @memberof ContentShellComponent
   */
  subscribeForRouteEvents() {
    this.subscription.add(this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loading = true;
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading = false;
          break;
        }
        default: {
          break;
        }
      }
    }));
  }

  /**
   * @name subscribeForTokenExpiry
   * @description Function to subscribe for view token expiry notification flag
   * @memberof ContentShellComponent
   */
  subscribeForTokenExpiry() {
    this.subscription.add(this.authService.tokenExpiryNotificationObservable
      .subscribe((view: boolean) => {
        if (view) {
          this.loadTokenExpiryComponent();
        } else {
          this.dynamicTokenExpiry.clear();
        }
      }));
  }

  /**
   * @name loadTokenExpiryComponent
   * @description Function to load token expiry component into DOM
   * @memberof ContentShellComponent
   */
  loadTokenExpiryComponent() {
    import('./token-expiry-notify/token-expiry-notify.component')
      .then(({ TokenExpiryNotifyComponent }) => {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(TokenExpiryNotifyComponent);
        this.dynamicTokenExpiry.clear();
        const componentRef = this.dynamicTokenExpiry.createComponent(componentFactory);
        componentRef.instance.viewTokenExpiryNotification = true;
      });
  }

  /**
   * @name loadNotificationComponent
   * @description Function to load notification component into DOM
   * @memberof ContentShellComponent
   */
  loadNotificationComponent() {
    import('./notification/notification.component')
      .then(({ NotificationComponent }) => {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(NotificationComponent);
        this.dynamicNotification.clear();
        this.notificationComponent = this.dynamicNotification.createComponent(componentFactory);
        this.notificationComponent.instance.notification$ = this.messageService.notification$;
        this.subscribeToHideNotificationEvent();
      });
  }

  /**
   * @name subscribeToHideNotificationEvent
   * @description Function to subscribe to hide notification event
   * @memberof ContentShellComponent
   */
  subscribeToHideNotificationEvent() {
    this.subscription.add(this.notificationComponent.instance.hideNotification
      .subscribe(() => {
        this.messageService.closeNotification();
      }));
  }

  ngOnDestroy() {
    this.dynamicTokenExpiry.clear();
    this.dynamicNotification.clear();
    this.subscription.unsubscribe();
  }

}
